import { FC, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { KTSVG } from '../../../_metronic/helpers'
import { LoaderOverlay } from '../../hooks/loader/LoaderOverlay'
import { showErrorAlert } from '../../helpers/alert'
import { UserTeamCardItem } from './components/UserTeamCardItem'
import { TeamMember } from './models/UserTeamModel'

import {
    moderationTeamCheck,
    createModerationTeam,
    deleteTeam,
} from '../../services/teamAdminService'
import { getTeamByTeamId, updateUserTeamForFinalUser } from '../../services/teamService'
import Pagination from '../components/Pagination'

interface Props {
    userTeamId:number
    firstName:string
    lastName:string
    showHeader?:boolean
}

const MyTeamWrapper: FC<Props> = ({ userTeamId, firstName, lastName, showHeader = true }) => {
    const history = useHistory()

    const [isLoader, setIsLoader] = useState(false)
    const [users, setUsers] = useState<TeamMember[]>([])
    const [teamId, setTeamId] = useState(0)

    // pagination
    const limit = 10
    const [currentPage, setCurrentPage] = useState(1)
    const [paginationCount, setPaginationCount] = useState(0)

    const totalPages = paginationCount
        ? Math.ceil(paginationCount / limit)
        : 1

    useEffect(() => {
        initTeam()
    }, [userTeamId])

    /**
     * Check if user has a team, if not create it
     */
    const initTeam = async () => {
        try {
            setIsLoader(true)

            const teams = await moderationTeamCheck({ user_id: userTeamId })
            let team = teams?.length > 0 ? teams[0] : null
            if (!team || !team?.isTargetUserOwner) {
                team = await createModerationTeam({
                    name: `My Team ${userTeamId ?? ''}`,
                    description: `Team created for ${firstName ?? ''} ${lastName ?? ''}`,
                    category: 'INTERNAL',
                    owner: userTeamId,
                })
            }

            if (team?.id) {
                setTeamId(team.id)
                await getUsers(team.id, 1, 0)
            }
        } catch (error) {
            showErrorAlert({ title: 'Error', message: 'Unable to load your team.' })
            setIsLoader(false)
        }
    }

    /**
     * Get team users with pagination
     */
    const getUsers = async (id:number, page:number = 1, offset: number = 0) => {
        try {
            setIsLoader(true)
            const response = await getTeamByTeamId(id)
            setUsers(response?.results ?? [])
            setPaginationCount(response?.count ?? 0)
            setCurrentPage(page)
        } finally {
            setIsLoader(false)
        }
    }

    /**
     * Pagination handler
     */
    const handlePageChange = async (page: number) => {
        const offset = (page - 1) * limit
        await getUsers(teamId, page, offset)
    }

    const onHandleActive = async (user:TeamMember, isActive: boolean) => {
        let data = { user_id: user?.id, is_active: isActive };
        let updateStatus = await updateUserTeamForFinalUser(teamId, user?.id, data);
        if(updateStatus?.id){
            showErrorAlert({ title: 'Action completed', message: `The team member was ${isActive ? 'activated' : 'deactivated'} successfully.` });
            setUsers((prevUsers) =>
                prevUsers.map((u) =>
                    u.id === user.id
                        ? {
                            ...u,
                            active: isActive,
                            isActive: isActive,
                            userData: { ...(u.userData || {}), active: isActive }
                        }
                        : u
                )
            );
        } else {
            getUsers(teamId, currentPage);
        }
    }

    const onHandleDelete = async (user:TeamMember) => {
        let updateStatus = await deleteTeam(teamId,user?.id);
        console.log('delete status', updateStatus)
        showErrorAlert({ title: 'Action completed', message: 'The team member was successfully deleted.' });
        getUsers(teamId);
    }

    return (
        <>
            <div className="my-team-page">
                {/* Header */}
                <div className={`my-team-page-header ${showHeader ? 'd-flex' : 'd-none'}` }>
                    <div className="my-team-page-title-wrapper">
                        <h1 className="my-team-page-title">My Team</h1>
                        <p className="my-team-page-description">
                            You can manage your Nobilis profile personally or invite trusted
                            individuals—such as personal assistants, agents, or coordinators—
                            to help manage your account.
                        </p>
                    </div>
                </div>

                {/* Actions */}
                <div className="my-team-page-actions">
                    {users.length > 0 && (
                        <div
                            className="tap-add-experience-2-btn-main"
                            onClick={() => history.push(`/user/${userTeamId}/my-team/add`)}
                        >
                            <div className="tap-host-button-text tap-flex-center">
                                add team member &nbsp;
                                <KTSVG path="/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg" />
                            </div>
                        </div>
                    )}
                </div>

                {/* Team list */}
                {users.length > 0 && (
                    <>
                        <div className="my-team-page-list">
                            {users.map((user, index) => (
                                <UserTeamCardItem
                                    key={`items-card-team-${index}`}
                                    user={user}
                                    onActive={onHandleActive}
                                    onDelete={onHandleDelete}
                                    onEdit={() =>
                                        // showErrorAlert({ title: 'Coming soon.', message: '' })
                                        history.push(`/user/${userTeamId}/my-team/edit/${user.id}`)
                                    }
                                />
                            ))}
                        </div>

                        {paginationCount > limit && (
                            <div className="tap-flex-center mt-5">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />
                            </div>
                        )}
                    </>
                )}

                {/* Empty state */}
                {users.length === 0 && !isLoader && (
                    <>
                        <p className="text-dark my-team-page-description">
                            You have no team members yet, add them now
                        </p>
                        <div className="tap-add-experience-2-btn-main w-fit" onClick={() => history.push(`/user/${userTeamId}/my-team/add`)}>
                            <div className="tap-host-button-text tap-flex-center">
                                add team member &nbsp;
                                <KTSVG path="/media/svg/nobilis/teams_and_partner/waitlist_nb_btn_icon_host_experience_btn.svg" />
                            </div>
                        </div>
                    </>
                )}
            </div>

            <LoaderOverlay visible={isLoader} message="loading..." />
        </>
    )
}

export { MyTeamWrapper }